import i18n from '@/plugins/translationsPlugin'
import { storeToRefs } from 'pinia'
import { v4 as uuidv4 } from 'uuid'
import { logService } from '@nsftx/seven-client-retail-utils'
import { useDeviceManagementStore } from '@nsftx/seven-client-retail-vue-dm'
import { useVoucher, voucherErrorParser, type Voucher } from '@nsftx/seven-client-retail-vue-sgp'
import { useSpinnerStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import { useTicketCheckStore } from '@/stores/ticket-check/ticketCheckStore'
import { useVoucherSSBTStore } from '@/stores/voucher/voucherSSBTStore'
import { useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import printService from '../print/services/printService'

const LOG_PREFIX = '[voucherSSBTService]'
const VOUCHER_CHECK_ERROR_NOTIFICATION_ID = 'voucher_check_notification_error'
const { t } = i18n.global

const handleScannedVoucher = async (barcode: string) => {
  const spinnerStore = useSpinnerStore()
  const notificationsStore = useNotificationsStore()
  const { appData } = storeToRefs(useDeviceManagementStore())
  const ticketCheckStore = useTicketCheckStore()
  const voucherSSBTStore = useVoucherSSBTStore()
  const { fetchVoucher } = useVoucher()

  notificationsStore.closeNotificationById(VOUCHER_CHECK_ERROR_NOTIFICATION_ID)

  try {
    spinnerStore.showSpinner()

    await fetchVoucher(appData.value!.betshop.companyUuid, barcode)

    ticketCheckStore.closeTicketCheckModal()
    voucherSSBTStore.showVoucherDetailsModal()
  } catch {
    logService.warn(`${LOG_PREFIX} Failed to check scanned voucher code`, {
      voucher_code: barcode,
      code: 'ST_VOUCHER_CHECK_SCAN_ERROR'
    })
    notificationsStore.showNotification({
      id: VOUCHER_CHECK_ERROR_NOTIFICATION_ID,
      text: [t('voucher_check_error')],
      variant: 'error'
    })
  } finally {
    spinnerStore.closeSpinner()
  }
}

const handleCheckedVoucher = async (barcode: string, cleanUpForm: () => void) => {
  const notificationsStore = useNotificationsStore()
  const { appData } = storeToRefs(useDeviceManagementStore())
  const ticketCheckStore = useTicketCheckStore()
  const voucherSSBTStore = useVoucherSSBTStore()
  const { fetchVoucher } = useVoucher()

  try {
    await fetchVoucher(appData.value!.betshop.companyUuid, barcode)

    cleanUpForm()
    ticketCheckStore.closeTicketCheckModal()
    voucherSSBTStore.showVoucherDetailsModal()
  } catch {
    logService.warn(`${LOG_PREFIX} Failed to check form voucher code`, {
      voucher_code: barcode,
      code: 'ST_VOUCHER_CHECK_FORM_ERROR'
    })
    notificationsStore.showNotification({
      id: VOUCHER_CHECK_ERROR_NOTIFICATION_ID,
      text: [t('voucher_check_error')],
      variant: 'error'
    })
  }
}

const printVoucher = async (voucherData: Voucher, context: string) => {
  const { showNotification } = useNotificationsStore()

  try {
    const printOptions = {
      meta: {
        requestId: uuidv4(),
        productDisplayId: 'Voucher'
      },
      model: voucherData
    }

    await printService.getPrintService().performPrint('voucher', context, printOptions)

    showNotification({
      variant: 'success',
      text: [t('voucher_print_success')]
    })
  } catch (err) {
    showNotification({
      variant: 'error',
      text: [t('voucher_print_failed')]
    })
    logService.error(`${LOG_PREFIX} Failed to print voucher`, {
      ...voucherErrorParser.parseUpstream(err),
      code: 'ST_VOUCHER_PRINT_ERROR'
    })
  }
}

export { handleScannedVoucher, handleCheckedVoucher, printVoucher }
